import { Box, Grid, Paper, Stack, styled, Typography } from "@mui/material";
import { api, types } from "api";
import { Curriculum, GroupResultProps } from "api/types";
import { AssignmentModelList } from "components/AssignmentModelList";
import { CommentList } from "components/CommentList";
import { CurriculumCard } from "components/CurriculumCard";
import { GroupDownloadDialog } from "components/DownloadDialog/GroupDownloadDialog";
import { InfoSnackBar } from "components/InfoSnackBar";
import { Assignment, getStatus } from "helpers/assignment";
import { useBreadcrumbs } from "helpers/useBreadcrumbs";
import { GET_RESULT_FAIL } from "messages";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

const defaultGroupResultProps: GroupResultProps = {
	date: "",
	name: "",
	yearLevel: "",
	totalAssignment: "",
	totalCorrect: "",
	totalAnswered: "",
	groupAssignmentResults: [],
	curriculumResults: [],
	comments: [],
};

const PREFIX = "GroupResultPage";

const classes = {
	root: `${PREFIX}-root`,
	button: `${PREFIX}-button`,
	title: `${PREFIX}-title`,
	groupList: `${PREFIX}-groupList`,
	summaryCard: `${PREFIX}-summaryCard`,
	summaryTitle: `${PREFIX}-summaryTitle`,
	summaryText: `${PREFIX}-summaryText`,
	icon: `${PREFIX}-icon`,
	bluePaper: `${PREFIX}-bluePaper`,
	Card: `${PREFIX}-Card`,
};

const Root = styled("div")(({ theme }) => ({
	[`&.${classes.root}`]: {
		padding: 20,
	},
	[`& .${classes.summaryCard}`]: {
		marginTop: 30,
		Height: 168,
		padding: theme.spacing(10),
		Width: 1200,
		marginRight: theme.spacing(4),
	},
	[`& .${classes.Card}`]: {
		marginTop: 30,
		Height: 100,
		padding: theme.spacing(10),
		Width: 1200,
		marginRight: theme.spacing(4),
	},
	[`& .${classes.bluePaper}`]: {
		marginTop: 30,
		minHeight: 80,
		padding: theme.spacing(2),
		minWidth: 200,
		marginRight: theme.spacing(4),
		// backgroundColor: blue[900]
		backgroundColor: "#09203F",
	},
}));

export const GroupResultPage = (): JSX.Element => {
	const [loading, setLoading] = useState(true);
	const [docTitle, setDocTitle] = useState("");
	const { updateLastBreadcrumbs } = useBreadcrumbs();
	const [groupResultProps, setGroupResultProps] = useState<GroupResultProps>(
		defaultGroupResultProps,
	);
	const [assignments, setAssignment] = useState<Assignment[]>([]);
	const [curriculums, setCurriculum] = useState<Curriculum[]>([]);
	const [err, setErr] = useState("");
	const [yearLevel, setYearLevel] = useState("");
	const [totalAssignment, setTotalAssignment] = useState("");
	const [totalAnswered, setTotalAnswered] = useState("");
	const { groupId } = useParams<{ groupId: types.UUID }>();

	const componentMounted = useRef(true);

	const fetchResult = useCallback(() => {
		api
			.getGroupResult(groupId)
			.then((res) => {
				if (res.status !== 200) {
					throw new Error();
				}
				setErr("");
				setDocTitle("Group " + res.data.name + " Academic Result");
				setYearLevel(String(res.data.yearLevel));
				setTotalAssignment(String(res.data.totalAssignment));
				// setTotalCorrect(String(res.data.totalCorrect));
				setTotalAnswered(String(res.data.totalAnswered));
				setGroupResultProps({
					name: res.data.name,
					date: res.data.date,
					yearLevel: String(res.data.yearLevel),
					totalAssignment: String(res.data.totalAssignment),
					totalCorrect: String(res.data.totalCorrect),
					totalAnswered: String(res.data.totalAnswered),
					groupAssignmentResults: res.data.groupAssignmentResultList,
					curriculumResults: res.data.curriculumResultList,
					comments: res.data.commentList === null ? [] : res.data.commentList,
				});
				if (res.data.curriculumResultList !== null) {
					setCurriculum(
						res.data.curriculumResultList.map((a) => a.curriculumDetails),
					);
				} else {
					setCurriculum([]);
				}
				setLoading(false);
				if (res.data) {
					updateLastBreadcrumbs({
						name: "Result for Group " + res.data.name,
					});
				}
			})
			.catch(() => {
				setErr(GET_RESULT_FAIL);
			});
	}, [updateLastBreadcrumbs, groupId]);

	useEffect(() => {
		api
			.getAssignments()
			.then((res) => {
				if (res.status !== 200) {
					setErr("");
					return;
				}

				if (componentMounted.current) {
					const now = new Date();
					setAssignment(
						(res.data || []).map((ass) => ({
							...ass,
							status: getStatus(ass, now),
						})),
					);
				}
			})
			.catch(() => {
				setErr("");
			});
		return () => {
			componentMounted.current = false;
		};
	}, []);

	useEffect(() => {
		fetchResult();
	}, [fetchResult]);

	const now = new Date();
	const formattedDate = now
		.toLocaleDateString("en-GB", {
			day: "numeric",
			month: "numeric",
			year: "numeric",
		})
		.replace(/ /g, "-");

	return (
		<Root className={classes.root}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<InfoSnackBar
						isOpen={err !== ""}
						severity="error"
						onClose={() => setErr("")}
						message={err}
					/>
				</Grid>
				<Grid item xs={6}>
					<Typography variant="h2" data-testid="hello">
						{`Group`}
					</Typography>
				</Grid>

				<Grid item xs={12}>
					<Paper className={classes.Card}>
						<Stack direction="row" spacing={10}>
							<Grid item xs={7}>
								<Stack direction="row" spacing={10}>
									<Typography variant="h4">Year Level: {yearLevel}</Typography>
									<Typography variant="h4">
										Assigned: {totalAssignment}
									</Typography>
									<Typography variant="h4">
										Completed: {totalAnswered}
									</Typography>
								</Stack>
							</Grid>
							<Grid item xs={3}>
								<Typography variant="h4">
									Report Date: {formattedDate}
								</Typography>
							</Grid>

							<GroupDownloadDialog
								loading={loading}
								baseName={docTitle}
								resultToBeRendered={groupResultProps}
							/>
						</Stack>
					</Paper>
				</Grid>
				<Grid item xs={12}>
					<Typography variant="h3" data-testid="hello">
						Recent Assignments
					</Typography>
				</Grid>
				{/* <Grid item xs={12}>
					<Paper className={classes.summaryCard}>

					</Paper>
					</Grid> */}
				<Grid item xs={12}>
					{assignments.length === 0 ? (
						<Typography variant="h5" className={classes.title}>
							No ongoing assignments
						</Typography>
					) : (
						<AssignmentModelList assignments={assignments} show_csv={false} />
					)}
				</Grid>
				<Grid item xs={12}>
					<Typography variant="h3" data-testid="hello">
						Curriculums
					</Typography>
				</Grid>
				<Grid item xs={12}>
					<Paper className={classes.summaryCard}>
						<Box
							display="grid"
							gap={2}
							gridTemplateColumns="repeat(auto-fit, minmax(300px, 1fr))"
						>
							{curriculums.length === 0 ? (
								<Typography variant="h4">No ongoing curriculums</Typography>
							) : (
								curriculums.map((item) => (
									<Box key={`${item.id} ${item.code} ${item.name}`}>
										<CurriculumCard curriculum={item} />
									</Box>
								))
							)}
						</Box>
					</Paper>
				</Grid>
				<Grid item xs={12}>
					<Typography variant="h3" data-testid="hello">
						Comments
					</Typography>
					<CommentList commentList={groupResultProps.comments} />
				</Grid>
			</Grid>
		</Root>
	);
};
